import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface HeatmapData {
  day: number // 0-6 (Sun-Sat)
  hour: number // 0-23
  count: number
}

interface ChatHeatmapProps {
  data: HeatmapData[]
  loading?: boolean
  title?: string
  description?: string
  tooltipLabel?: string
}

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const hours = Array.from({ length: 24 }, (_, i) => i)

export default function ChatHeatmap({ data, loading = false, title = 'Chat Volume Heatmap', description = 'Identify peak hours and optimize agent scheduling', tooltipLabel = 'chat' }: ChatHeatmapProps) {
  // Create a matrix for easy lookup
  const matrix = React.useMemo(() => {
    const m: number[][] = Array(7).fill(0).map(() => Array(24).fill(0))
    data.forEach(({ day, hour, count }) => {
      if (day >= 0 && day < 7 && hour >= 0 && hour < 24) {
        m[day][hour] = count
      }
    })
    return m
  }, [data])

  // Find max for color scaling
  const maxCount = React.useMemo(() => {
    let max = 0
    matrix.forEach(row => {
      row.forEach(count => {
        if (count > max) max = count
      })
    })
    return max || 1
  }, [matrix])

  const getIntensity = (count: number) => {
    if (count === 0) return 0
    return Math.max(0.1, count / maxCount)
  }

  const getColor = (intensity: number) => {
    if (intensity === 0) return 'bg-muted'
    if (intensity < 0.25) return 'bg-brand-primary-100'
    if (intensity < 0.5) return 'bg-brand-primary-300'
    if (intensity < 0.75) return 'bg-brand-primary-500'
    return 'bg-brand-primary-600'
  }

  const formatHour = (hour: number) => {
    if (hour === 0) return '12am'
    if (hour < 12) return `${hour}am`
    if (hour === 12) return '12pm'
    return `${hour - 12}pm`
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading heatmap...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <div className="space-y-4">
            {/* Legend */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Low activity</span>
              <div className="flex items-center gap-1">
                <div className="h-3 w-3 rounded bg-brand-primary-100" />
                <div className="h-3 w-3 rounded bg-brand-primary-300" />
                <div className="h-3 w-3 rounded bg-brand-primary-500" />
                <div className="h-3 w-3 rounded bg-brand-primary-600" />
              </div>
              <span>High activity</span>
            </div>

            {/* Heatmap */}
            <div
              className="overflow-x-auto"
              style={{ touchAction: 'pan-x', overscrollBehaviorX: 'contain' }}
            >
              <div className="inline-block min-w-full">
                {/* Hour labels */}
                <div className="flex mb-1">
                  <div className="w-12" />
                  {hours.map((hour) => (
                    <div
                      key={hour}
                      className="flex-shrink-0 text-center text-xs text-muted-foreground"
                      style={{ width: '32px' }}
                    >
                      {hour % 3 === 0 ? formatHour(hour) : ''}
                    </div>
                  ))}
                </div>

                {/* Days and cells */}
                {days.map((day, dayIndex) => (
                  <div key={day} className="flex items-center mb-1">
                    <div className="w-12 text-xs font-medium text-muted-foreground pr-2 text-right">
                      {day}
                    </div>
                    {hours.map((hour) => {
                      const count = matrix[dayIndex][hour]
                      const intensity = getIntensity(count)
                      const color = getColor(intensity)

                      const label = `${day}, ${formatHour(hour)} — ${count} ${count === 1 ? tooltipLabel : tooltipLabel + 's'}`
                      return (
                        <Tooltip key={hour}>
                          <TooltipTrigger asChild>
                            <div
                              title={label}
                              className={cn(
                                'flex-shrink-0 h-7 rounded transition-colors [@media(hover:hover)]:cursor-pointer [@media(hover:hover)]:hover:ring-2 [@media(hover:hover)]:hover:ring-brand-primary [@media(hover:hover)]:hover:scale-110',
                                color
                              )}
                              style={{ width: '30px', marginRight: '2px', touchAction: 'pan-y' }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <div className="font-semibold">
                                {day}, {formatHour(hour)}
                              </div>
                              <div className="text-muted-foreground">
                                {count} {count === 1 ? tooltipLabel : tooltipLabel + 's'}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Insights */}
            {maxCount > 0 && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">💡 Insight:</strong> Peak chat volume occurs
                  during business hours (9am-5pm) on weekdays. Consider adjusting agent schedules
                  accordingly.
                </p>
              </div>
            )}
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  )
}
